/**
 * useToolSidecar — same shape as useModuleSidecar but for mover tools.
 *
 * Lookup order: built-in compiled sidecar → HTTP fetch → identity.
 */

import { useEffect, useState } from 'react';
import {
  SidecarLoader,
  type MoverToolSidecar,
  type OriginCorrection,
  DEFAULT_ORIGIN_CORRECTION,
  toolSidecarFilename,
} from '../../assets/SidecarLoader.js';
import { BUILTIN_TOOL_SIDECARS } from '../../assets/builtinSidecars.js';
import { useSidecarSource } from '../../assets/SidecarSourceContext.js';
import { composeAssetUrl } from '../../assets/AssetLoader.js';
import type { MoverToolType3D } from '../../types.js';

const SHARED = new SidecarLoader();

export function useToolSidecar(
  toolType: MoverToolType3D,
  assetsBaseUrl: string,
): { sidecar: MoverToolSidecar | undefined; correction: OriginCorrection } {
  const { useBuiltins } = useSidecarSource();
  const builtin =
    toolType === 'Custom' || !useBuiltins
      ? undefined
      : BUILTIN_TOOL_SIDECARS[toolType];
  const [sidecar, setSidecar] = useState<MoverToolSidecar | undefined>(
    builtin,
  );

  useEffect(() => {
    if (toolType === 'Custom') {
      setSidecar(undefined);
      return;
    }
    if (builtin) {
      setSidecar(builtin);
      return;
    }
    let alive = true;
    const url = composeAssetUrl(assetsBaseUrl, toolSidecarFilename(toolType));
    SHARED.fetchTool(url)
      .then((s) => {
        if (alive) setSidecar(s);
      })
      .catch(() => {
        if (alive) setSidecar(undefined);
      });
    return () => {
      alive = false;
    };
  }, [toolType, assetsBaseUrl, builtin]);

  return {
    sidecar,
    correction: sidecar?.originCorrection ?? DEFAULT_ORIGIN_CORRECTION,
  };
}
